/**
 * ClinicsManagement - Standalone Clinics Management Component
 *
 * Manages clinics with CRUD operations using DataCard for display.
 * Uses ClinicsModalV3 which supports time-windowed device associations.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner, Badge } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import ClinicsModalV3 from './modals/ClinicsModalV3';
import EntityCSVUploadButton from './EntityCSVUploadButton';
import type { Clinic, Device } from '@/types/associations';

export default function ClinicsManagement() {
  // Entity state
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Modal state
  const [modalState, setModalState] = useState<{ show: boolean; editing: Clinic | null }>({
    show: false,
    editing: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clinicsRes, devicesRes] = await Promise.all([
        apiClient.getAdminClinics(),
        apiClient.getAdminDevices(),
      ]);
      if (!clinicsRes.error && clinicsRes.data) {
        setClinics(clinicsRes.data as Clinic[]);
      }
      if (!devicesRes.error && devicesRes.data) {
        setDevices(devicesRes.data as Device[]);
      }
    } catch (error) {
      toast.error('Failed to load clinics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD handlers
  const handleSave = async (data: Partial<Clinic>) => {
    if (modalState.editing) {
      const response = await apiClient.updateAdminClinic(modalState.editing.id, data);
      if (response.error) throw new Error(response.error);
      toast.success('Clinic updated successfully');
    } else {
      if (!data.clinic_id || !data.name) {
        throw new Error('Clinic ID and name are required');
      }
      const clinicPayload: {
        clinic_id: string;
        name: string;
        description?: string;
        location?: string;
        is_active?: boolean;
      } = {
        clinic_id: String(data.clinic_id).trim(),
        name: String(data.name).trim(),
      };
      if (data.description && data.description.trim()) {
        clinicPayload.description = data.description.trim();
      }
      const dataWithLocation = data as Partial<Clinic> & { location?: string; is_active?: boolean };
      if (dataWithLocation.location?.trim() && dataWithLocation.location.trim().toUpperCase() !== 'NA') {
        clinicPayload.location = dataWithLocation.location.trim();
      }
      if (dataWithLocation.is_active !== undefined) {
        clinicPayload.is_active = dataWithLocation.is_active;
      }
      const response = await apiClient.createAdminClinic(clinicPayload);
      if (response.error) throw new Error(response.error);
      toast.success('Clinic created successfully');
    }
    await loadData();
  };

  const handleDelete = (id: number) => {
    const clinic = clinics.find((c) => c.id === id);
    setConfirmModal({
      show: true,
      title: 'Delete Clinic',
      message: `Are you sure you want to delete "${clinic?.name || clinic?.clinic_id}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteAdminClinic(id);
          if (response.error) throw new Error(response.error);
          toast.success('Clinic deleted successfully');
          setSelectedId(null);
          await loadData();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete clinic');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Table rendering
  const renderTable = (items: Clinic[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Clinic ID</th>
          <th>Location</th>
          <th>Devices</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((clinic) => (
          <tr
            key={clinic.id}
            onClick={() => setSelectedId(selectedId === clinic.id ? null : clinic.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedId === clinic.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><strong>{clinic.name || clinic.clinic_id}</strong></td>
            <td className="text-muted small">{clinic.clinic_id}</td>
            <td className="text-muted small">{clinic.location || '-'}</td>
            <td><Badge bg="info">{clinic.device_count || 0}</Badge></td>
            <td>
              <span className={`badge ${clinic.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                {clinic.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading clinics...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Clinics Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage clinics and their device associations
          </p>
        </div>
      </div>

      <DataCard
        cardKey="clinics"
        title="Clinics"
        badgeCount={clinics.length}
        data={clinics}
        itemsPerPage={10}
        searchPlaceholder="Search clinics..."
        filterFunction={(item, searchTerm) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          return (
            item.name?.toLowerCase().includes(term) ||
            item.clinic_id.toLowerCase().includes(term) ||
            (item.location || '').toLowerCase().includes(term)
          );
        }}
        headerActions={
          <>
            {selectedId !== null && (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    const clinic = clinics.find((c) => c.id === selectedId);
                    if (clinic) setModalState({ show: true, editing: clinic });
                  }}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    if (selectedId !== null) handleDelete(selectedId);
                  }}
                  className="me-2"
                >
                  Delete
                </Button>
              </>
            )}
            <EntityCSVUploadButton
              entityType="clinics"
              onUploadComplete={loadData}
              size="sm"
              variant="outline-success"
              className="me-2"
            />
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalState({ show: true, editing: null })}
            >
              Create
            </Button>
          </>
        }
        renderTable={renderTable}
        onRefresh={loadData}
        maxHeight="600px"
        emptyMessage="No clinics found. Create your first clinic."
      />

      {/* Clinic Modal */}
      <ClinicsModalV3
        show={modalState.show}
        clinic={modalState.editing}
        devices={devices}
        onHide={() => setModalState({ show: false, editing: null })}
        onSave={handleSave}
        onRefresh={loadData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
